import React from 'react';
import { Helmet } from '@lib/helmet-shim';
import { getRobotsMeta, SEO_CONFIG, siteConfig } from '../config';

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

interface SeoHeadProps {
  title?: string;
  description?: string;
  pageSlug?: string; // 'terms', 'dmca', 'privacy', etc.
  image?: string;
  url?: string;
  canonicalUrl?: string;
  jsonLd?: Record<string, unknown>;
  hreflangAlternates?: HreflangAlternate[];
}

/**
 * SEO Head Component
 * Handles all meta tags including dynamic robots meta for different page types
 * Automatically applies noindex,nofollow for terms/dmca/privacy pages
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  pageSlug,
  image = SEO_CONFIG.DEFAULT_IMAGE,
  url,
  canonicalUrl,
  jsonLd,
  hreflangAlternates,
}) => {
  const robotsMeta = getRobotsMeta(pageSlug);
  // If title already contains the separator (e.g. seoTitle with custom format),
  // use it as-is; otherwise append the default suffix.
  const fullTitle = title
    ? title.includes(SEO_CONFIG.TITLE_SEPARATOR)
      ? title
      : `${title}${SEO_CONFIG.TITLE_SEPARATOR}${SEO_CONFIG.TITLE_SUFFIX}`
    : undefined;
  const canonical = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : siteConfig.siteUrl);
  // Social crawlers require absolute URLs — prepend siteUrl for relative paths
  const absoluteImage = image.startsWith('http') ? image : `${siteConfig.siteUrl}${image}`;

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsMeta} />
      <link rel="canonical" href={canonical} />

      {/* Hreflang alternates for multi-language / x-default signaling */}
      {hreflangAlternates?.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.siteName} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:alt" content={fullTitle || siteConfig.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.OG_LOCALE} />
      <meta property="og:url" content={canonical} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={SEO_CONFIG.TWITTER_CARD_TYPE} />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={fullTitle || siteConfig.siteName} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
